/**
 * UserProfileAccessTable Component
 *
 * Presentational React component that displays a user's tool and role assignments
 * in a sortable MUI table with two columns (Tool, Role). Filters data from Redux
 * state by user email, sorts alphabetically, handles empty states, and applies
 * accessible markup with ARIA labels following the established UserManagementTable
 * styling patterns.
 *
 * Features:
 * - Filters user data from Redux store by email address
 * - Sorts alphabetically by Tool (primary) then Role (secondary)
 * - Displays two-column table with Tool and Role headers
 * - Handles empty state with internationalized message
 * - Uses MUI Table components with accessible semantic markup
 * - Applies RSM design tokens for consistent styling
 * - Full WCAG 2.1 AA accessibility compliance with ARIA labels
 * - Zebra striping for alternating rows
 * - Text overflow handling with ellipsis
 *
 * @component
 */

import React, { useCallback, useMemo, useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getUsersData } from '../../store/userManagement/userManagementSlice'
import { UserApplicationData } from '../../store/userManagement/userManagementSearchService'
import tokens from '../../styles/tokens.json'
import { Styles } from '../../types'
import SortableHeader from '../../rsmCoreComponents/components/SortableHeader/SortableHeader'

/**
 * Styles for UserProfileAccessTable component
 * Following UserManagementTable.tsx pattern for structural consistency
 * Implements responsive design and RSM design tokens
 */
const styles: Styles = {
  userProfileAccessTable: (theme) => ({
    // Base table styles consistent with UserManagementTable
    '&.MuiTable-root': {
      tableLayout: 'fixed',
      width: '100%',
      '& .MuiTableCell-root': {
        borderBottom: 'none',
      },
      '& th.MuiTableCell-root': {
        fontFamily: 'Prelo-Bold, sans-serif',
      },
      '& td.MuiTableCell-root': {
        fontFamily: 'Prelo-Book, sans-serif',
      },
      '& .MuiTableCell-head': {
        padding: '1rem',
        fontSize: '0.875rem',
        lineHeight: '1rem',
        textTransform: 'uppercase',
        verticalAlign: 'top',
        alignItems: 'center',
      },
      '& tbody': {
        '& .MuiTableRow-root': {
          '&:nth-of-type(odd)': {
            backgroundColor: tokens.colors.rsmGray.accessibility,
          },
        },
      },
      '& .MuiTableCell-body': {
        padding: '1.5rem 1rem',
        fontSize: '1rem',
        lineHeight: '1.5rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    // Two-column layout: Tool (60%), Role (40%)
    [theme.breakpoints.up('desktop')]: {
      '& .MuiTableCell-root': {
        '&:nth-of-type(1)': { width: '60%' }, // Tool column
        '&:nth-of-type(2)': { width: '40%' }, // Role column
      },
    },
  }),
  emptyStateContainer: {
    padding: 4,
    textAlign: 'center',
    backgroundColor: tokens.colors.rsmGray.accessibility,
    borderRadius: 1,
    marginTop: 2,
    marginBottom: 3,
  },
  emptyStateText: {
    fontFamily: 'Prelo-Book, sans-serif',
    color: tokens.colors.rsmText.primary,
    fontSize: '1rem',
  },
  visuallyHidden: {
    position: 'absolute',
    width: '0.0625rem',
    height: '0.0625rem',
    padding: 0,
    margin: '-0.0625rem',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}

/**
 * Props interface for UserProfileAccessTable component
 */
interface UserProfileAccessTableProps {
  /**
   * Email address of the user to display tool/role assignments for.
   * Used to filter the Redux store data to show only this user's assignments.
   */
  userEmail: string
}

/**
 * Interface for processed tool/role assignment data
 * Represents a single tool-role combination for display in the table
 */
interface ToolRoleAssignment {
  /** Tool/application name */
  tool: string
  /** Role name assigned to the user for this tool */
  role: string
}

/**
 * UserProfileAccessTable Component
 *
 * Displays user tool and role assignments in an accessible, sortable table.
 * Data is filtered by user email and sorted alphabetically.
 *
 * @param props - Component props
 * @param props.userEmail - Email address to filter user assignments
 * @returns Rendered table component or empty state message
 */
const UserProfileAccessTable = ({ userEmail }: UserProfileAccessTableProps) => {
  const { t } = useTranslation()
  const [sortDirection, setSortDirection] = useState<string>('asc')
  const [sortColumn, setSortColumn] = useState<string>('tool')
  const [isMounted, setIsMounted] = useState<boolean>(false)

  // Retrieve all users data from Redux store using getUsersData selector
  const allUsersData = useSelector(getUsersData)

  /**
   * Memoized computation of filtered and sorted tool/role assignments
   * Performance optimization: Only recalculates when userEmail or allUsersData changes
   *
   * Process:
   * 1. Filter all user records to find entries matching the provided email
   * 2. Extract tool and role from each matching record
   * 3. Sort alphabetically by tool (primary), then by role (secondary)
   *
   * @returns Array of ToolRoleAssignment objects sorted alphabetically
   */
  const toolRoleAssignments = useMemo((): ToolRoleAssignment[] => {
    // Return empty array if no email provided or no data available
    if (!userEmail || !allUsersData || allUsersData.length === 0) {
      return []
    }

    // Filter all records to find entries matching the user's email (case-insensitive)
    const userRecords = allUsersData.filter(
      (userData: UserApplicationData) =>
        userData.email?.toLowerCase() === userEmail.toLowerCase(),
    )

    // Extract tool/role combinations from all matching records
    const assignments: ToolRoleAssignment[] = []

    userRecords.forEach((record: UserApplicationData) => {
      // Access the tool property from UserApplicationData interface
      const tool = record.tool || ''
      const role = record.role || ''

      // Only add assignments that have at least a tool or role defined
      if (tool || role) {
        assignments.push({ tool, role })
      }
    })

    // Sort alphabetically: primary by tool name, secondary by role name
    // Uses localeCompare for proper string comparison with internationalization support
    const sorted = assignments.sort((a, b) => {
      const toolComparison = a.tool.localeCompare(b.tool)
      // If tools are different, return tool comparison result
      if (toolComparison !== 0) {
        return toolComparison
      }
      // If tools are the same, compare by role
      return a.role.localeCompare(b.role)
    })

    setIsMounted(true)

    return sorted
  }, [userEmail, allUsersData])

  const handleSort = useCallback(
    (columnName: string) => {
      const newSortDirection: 'asc' | 'desc' =
        sortColumn === columnName && sortDirection === 'asc' ? 'desc' : 'asc'
      const result = toolRoleAssignments.sort((a, b) => {
        let compareResult = 0

        // Primary sort by selected column
        const sortKey = columnName as keyof ToolRoleAssignment
        const aVal = a[sortKey].toLowerCase() || ''
        const bVal = b[sortKey].toLowerCase() || ''

        if (aVal < bVal) compareResult = -1
        if (aVal > bVal) compareResult = 1

        // Secondary sort by Tool if primary values are equal
        if (compareResult === 0 && columnName !== 'tool') {
          if (a.tool < b.tool) compareResult = -1
          if (a.tool > b.tool) compareResult = 1
        }

        return newSortDirection === 'asc' ? compareResult : -compareResult
      })

      setSortColumn(columnName)
      setSortDirection(newSortDirection)

      return result
    },
    [sortColumn, sortDirection],
  )

  const getSortableHeader = useCallback(
    (columnName: string, displayValue: string) => (
      <TableCell>
        <SortableHeader
          columnName={columnName}
          currentSortColumn={sortColumn || ''}
          sortDirection={sortDirection || ''}
          isMounted={isMounted}
          handleSort={handleSort}>
          {displayValue}
        </SortableHeader>
      </TableCell>
    ),
    [sortColumn, sortDirection, isMounted, handleSort],
  )

  /**
   * Handle empty state - no tools assigned to user
   * Displays internationalized message from translation keys
   */
  if (toolRoleAssignments.length === 0) {
    return (
      <Box sx={styles.emptyStateContainer}>
        <Typography
          variant="body1"
          data-testid="Lbl_NoToolsAssigned"
          sx={styles.emptyStateText}>
          {t('UserManagement.Profile.NoToolsAssigned')}
        </Typography>
      </Box>
    )
  }

  /**
   * Render table with tool/role assignments
   * Uses semantic HTML table elements with ARIA labels for accessibility
   * Applies consistent styling from UserManagementTable pattern
   */
  return (
    <TableContainer component={Paper}>
      <Table
        sx={styles.userProfileAccessTable}
        aria-label={t('UserManagement.Profile.AccessibilityLabels.AccessTable')}
        data-testid="Tbl_UserProfileAccess">
        {/* Visually hidden caption for screen readers (WCAG 2.1 AA compliance) */}
        <Box component="caption" sx={styles.visuallyHidden}>
          {`User has ${toolRoleAssignments.length} tool and role assignment${
            toolRoleAssignments.length !== 1 ? 's' : ''
          }`}
        </Box>

        <TableHead>
          <TableRow>
            {/* Column 1: Tool - displays application/tool name */}
            <TableCell>
              {getSortableHeader('tool', t('UserManagement.TableHeaders.Tool'))}
            </TableCell>
            {/* Column 2: Role - displays role assigned to user for this tool */}
            <TableCell>
              {getSortableHeader('role', t('UserManagement.TableHeaders.Role'))}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {toolRoleAssignments.map((assignment, index) => (
            <TableRow
              // Using index in key is acceptable here as the list is pre-sorted and stable
              // eslint-disable-next-line react/no-array-index-key
              key={`${assignment.tool}-${assignment.role}-${index}`}
              data-testid={`Row_ToolRole_${index}`}>
              {/* Tool column with text overflow handling */}
              <TableCell data-testid={`Cell_Tool_${index}`}>
                {assignment.tool}
              </TableCell>
              {/* Role column with text overflow handling */}
              <TableCell data-testid={`Cell_Role_${index}`}>
                {assignment.role}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default UserProfileAccessTable
